/**
 * UNIFIED CART RENDERER - UNIVERSAL SOLUTION
 * ==========================================
 * This script ensures CONSISTENT cart display across:
 * - Video carousel modal
 * - Product cards
 * - Side cart drawer
 * - All add-to-cart actions
 * 
 * Place this in a separate file: assets/unified-cart.js
 * Or add to theme.js
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  
  const CART_CONFIG = {
    sideCartSelector: '.side-cart',
    sideCartContentSelector: '.side-cart-content',
    sideCartFooterSelector: '.side-cart-footer',
    cartCountSelector: '[data-cart-count], .cart-count, .cart-count-bubble',
    retryAttempts: 3,
    retryDelay: 500
  };

  // ============================================================================
  // CART STATE
  // ============================================================================
  
  let isUpdating = false;
  let cartCache = null;
  let cacheExpiry = 0;

  // ============================================================================
  // UNIVERSAL CART ITEM HTML TEMPLATE
  // ============================================================================
  
  function createCartItemHTML(item, index) {
    const lineNumber = index + 1;
    
    return `
      <div class="product-item unified-cart-item" 
        data-id="${item.id}" 
        data-key="${item.key}" 
        data-line="${lineNumber}"
        data-variant-id="${item.variant_id}"
        data-product-id="${item.product_id}">
        
        ${item.image ? `
          <a href="${item.url || '#'}" class="product-image" tabindex="-1" aria-hidden="true">
            <img src="${item.image}" 
              alt="${escapeHtml(item.title)}" 
              loading="lazy" 
              width="150" />
          </a>
        ` : ''}
        
        <div class="product-info">
          <h3 class="product-name">
            <a href="${item.url || '#'}" tabindex="-1">
              ${escapeHtml(item.product_title)}
            </a>
          </h3>
          
          ${item.variant_title && item.variant_title !== 'Default Title' ? `
            <div class="product-options">
              <span class="product-option">
                <span class="option-value">${escapeHtml(item.variant_title)}</span>
              </span>
            </div>
          ` : ''}
          
          <div class="hidden-desktop product-price-mobile">
            <span class="price">${formatMoney(item.final_line_price)}</span>
          </div>
          
          <div class="bottom-line">
            <div class="quantity unified-quantity-control" role="group" aria-label="Quantity controls">
              <button class="quantity-button qty-minus" 
                type="button"
                data-id="${item.id}"
                data-key="${item.key}"
                data-qty="${item.quantity}"
                data-line="${lineNumber}"
                aria-label="Decrease quantity"
                ${item.quantity <= 1 ? 'disabled' : ''}>
                <span class="sr-only">Decrease quantity</span>
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
              
              <input class="quantity-input unified-quantity-input"
                type="number"
                name="quantity"
                min="1"
                max="999"
                step="1"
                data-id="${item.id}"
                data-key="${item.key}"
                data-line="${lineNumber}"
                value="${item.quantity}"
                aria-label="Quantity"
                data-noui>
              
              <button class="quantity-button qty-plus" 
                type="button"
                data-id="${item.id}"
                data-key="${item.key}"
                data-qty="${item.quantity}"
                data-line="${lineNumber}"
                aria-label="Increase quantity">
                <span class="sr-only">Increase quantity</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 1V11M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            
            <div class="hidden-tablet hidden-mobile product-price-desktop">
              <span class="price">${formatMoney(item.final_line_price)}</span>
            </div>
            
            <a href="#" 
              class="product-remove" 
              data-id="${item.id}"
              data-key="${item.key}"
              data-line="${lineNumber}"
              role="button"
              aria-label="Remove ${escapeHtml(item.title)}"
              tabindex="0">
              <span class="tooltip">Remove</span>
              <span class="remove-icon" aria-hidden="true">×</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  // ============================================================================
  // UNIVERSAL CART UPDATE FUNCTION
  // ============================================================================
  
  async function updateCartDisplay() {
    if (isUpdating) {
      console.log('⏳ Cart update already in progress');
      return;
    }
    
    isUpdating = true;
    
    try {
      // Fetch fresh cart data
      const cart = await getCart(true);
      
      // Update cart content
      renderCartContent(cart);
      
      // Update cart count
      updateCartCount(cart.item_count);
      
      // Reattach event listeners
      attachCartEventListeners();
      
      console.log('✅ Cart display updated successfully');
      
    } catch (error) {
      console.error('❌ Error updating cart display:', error);
    } finally {
      isUpdating = false;
    }
  }

  // ============================================================================
  // RENDER CART CONTENT
  // ============================================================================
  
  function renderCartContent(cart) {
    const contentEl = document.querySelector(CART_CONFIG.sideCartContentSelector);
    const footerEl = document.querySelector(CART_CONFIG.sideCartFooterSelector);
    
    if (!contentEl) {
      console.warn('⚠️ Cart content element not found');
      return;
    }
    
    // Handle empty cart
    if (!cart.items || cart.items.length === 0) {
      contentEl.innerHTML = '<p class="empty-cart-message">Your cart is currently empty.</p>';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }
    
    // Render cart items
    const itemsHTML = cart.items.map((item, index) => createCartItemHTML(item, index)).join('');
    contentEl.innerHTML = itemsHTML;
    
    // Update footer
    if (footerEl) {
      footerEl.style.display = 'block';
      const totalPriceEl = footerEl.querySelector('.total-price, [data-cart-total]');
      if (totalPriceEl) {
        totalPriceEl.textContent = formatMoney(cart.total_price);
      }
    }
  }

  // ============================================================================
  // GET CART DATA
  // ============================================================================
  
  async function getCart(forceRefresh = false) {
    // Return cached cart if valid
    if (!forceRefresh && cartCache && Date.now() < cacheExpiry) {
      return cartCache;
    }
    
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      
      const cart = await response.json();
      
      // Cache for 3 seconds
      cartCache = cart;
      cacheExpiry = Date.now() + 3000;
      
      return cart;
      
    } catch (error) {
      console.error('❌ Error fetching cart:', error);
      return { items: [], item_count: 0, total_price: 0 };
    }
  }

  // ============================================================================
  // UPDATE CART COUNT
  // ============================================================================
  
  function updateCartCount(count) {
    const elements = document.querySelectorAll(CART_CONFIG.cartCountSelector);
    elements.forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.style.display = 'inline-block';
        el.classList.remove('hidden');
      } else {
        el.style.display = 'none';
      }
    });
  }

  // ============================================================================
  // ATTACH EVENT LISTENERS
  // ============================================================================
  
  function attachCartEventListeners() {
    // Quantity buttons
    document.querySelectorAll('.unified-cart-item .qty-minus, .unified-cart-item .qty-plus').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        const input = btn.parentElement.querySelector('.quantity-input');
        let qty = parseInt(input.value) || 0;
        
        if (btn.classList.contains('qty-plus')) {
          qty += 1;
        } else if (btn.classList.contains('qty-minus') && qty > 0) {
          qty -= 1;
        }
        
        input.value = qty;
        await updateCartItemQuantity(btn.dataset.key, qty);
      };
    });
    
    // Quantity inputs
    document.querySelectorAll('.unified-cart-item .quantity-input').forEach(input => {
      input.onchange = async function() {
        let qty = parseInt(this.value) || 0;
        if (qty < 0) qty = 0;
        if (qty > 999) qty = 999;
        this.value = qty;
        
        await updateCartItemQuantity(this.dataset.key, qty);
      };
    });
    
    // Remove buttons
    document.querySelectorAll('.unified-cart-item .product-remove').forEach(btn => {
      btn.onclick = async (e) => {
        e.preventDefault();
        await updateCartItemQuantity(btn.dataset.key, 0);
      };
    });
  }

  // ============================================================================
  // UPDATE CART ITEM QUANTITY
  // ============================================================================
  
  async function updateCartItemQuantity(key, quantity) {
    if (isUpdating) return;
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      
      // Force refresh cart display
      await updateCartDisplay();
      
    } catch (error) {
      console.error('❌ Error updating cart item:', error);
      alert('Could not update cart. Please try again.');
    }
  }

  // ============================================================================
  // ADD TO CART FUNCTION
  // ============================================================================
  
  async function addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }
      
      const result = await response.json();
      
      // Update cart display
      await updateCartDisplay();
      
      // Open cart
      openCart();
      
      return result;
      
    } catch (error) {
      console.error('❌ Add to cart error:', error);
      throw error;
    }
  }

  // ============================================================================
  // OPEN/CLOSE CART
  // ============================================================================
  
  function openCart() {
    const sideCart = document.querySelector(CART_CONFIG.sideCartSelector);
    if (sideCart) {
      sideCart.classList.add('active');
      document.body.classList.add('cart-open', 'side-cart-open');
    }
  }
  
  function closeCart() {
    const sideCart = document.querySelector(CART_CONFIG.sideCartSelector);
    if (sideCart) {
      sideCart.classList.remove('active');
      document.body.classList.remove('cart-open', 'side-cart-open');
    }
  }

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '₹{{amount}}');
    }
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }
  
  function escapeHtml(text) {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }

  // ============================================================================
  // EXPOSE API
  // ============================================================================
  
  window.UnifiedCart = {
    update: updateCartDisplay,
    add: addToCart,
    open: openCart,
    close: closeCart,
    getCart: getCart,
    version: '1.0'
  };

  // ============================================================================
  // INITIALIZE
  // ============================================================================
  
  function init() {
    console.log('🚀 Initializing Unified Cart System...');
    
    // Update cart display on load
    updateCartDisplay();
    
    // Setup close button
    const closeBtn = document.querySelector('.side-cart-close');
    if (closeBtn) {
      closeBtn.onclick = closeCart;
    }
    
    // Setup overlay click
    const sideCart = document.querySelector(CART_CONFIG.sideCartSelector);
    if (sideCart) {
      sideCart.onclick = (e) => {
        if (e.target === sideCart) closeCart();
      };
    }
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCart();
    });
    
    // Listen for cart update events from other parts of the app
    document.addEventListener('cart:refresh', () => {
      updateCartDisplay();
    });
    
    console.log('✅ Unified Cart System ready');
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize on section load (for theme editor)
  document.addEventListener('shopify:section:load', init);

})();